import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config';
import SmartInput from './SmartInput';
import DraftItemRow from './DraftItemRow';
import NotificationSelector from './NotificationSelector';
import { parseSmartInput } from '../lib/smartInputParser';
import { applyShoppingSeedUpdate, hydrateShoppingSeedDefaults } from '../shared/shoppingSeedPricing';
import {
    resolveDraftNormalization,
    buildDraftNormalizationPayload,
    resolveDraftNormalizationPreview
} from '../shared/shoppingNormalization';

const QuickListEntryModal = ({ isOpen, onClose, onFinish, initialValue = '' }) => {
    const [draftItems, setDraftItems] = useState([]);
    const [editingDraftItem, setEditingDraftItem] = useState(null);
    const [listName, setListName] = useState('');
    const [notificationSettings, setNotificationSettings] = useState({
        enabled: false,
        type: 'push',
        date: '',
        time: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasteMode, setIsPasteMode] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [isResolvingBulk, setIsResolvingBulk] = useState(false);
    const MARKET_UNITS = new Set(['paket', 'kutu', 'koli', 'sise', 'şişe', 'file']);

    const hydrateDraftEditorItem = (item = {}) => {
        const hydrated = hydrateShoppingSeedDefaults(item);
        const normalization = resolveDraftNormalization(hydrated);
        return {
            ...hydrated,
            measurementType: normalization.measurementType,
            expectedCanonicalUnit: normalization.expectedCanonicalUnit,
            perUnitCanonicalAmount: hydrated.perUnitCanonicalAmount || '',
            learnedPreference: hydrated.learnedPreference || null,
            learnedPreferenceApplied: Boolean(hydrated.learnedPreferenceApplied)
        };
    };

    const fetchUnitPreference = async (item = {}) => {
        const unit = String(item?.unit || '').trim();
        if (!MARKET_UNITS.has(unit) || !item?.dictionaryId) return null;
        try {
            const token = localStorage.getItem('authToken');
            const params = new URLSearchParams({
                dictionaryId: String(item.dictionaryId),
                ingredientName: String(item.name || ''),
                marketUnit: unit
            });
            const res = await fetch(`${API_BASE_URL}/api/shopping/unit-preference?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const data = await res.json();
            return data?.preference || null;
        } catch (error) {
            console.error('Unit preference fetch error:', error);
            return null;
        }
    };

    const applyLearnedPreferenceToEditor = async (baseItem = {}) => {
        const preference = await fetchUnitPreference(baseItem);
        setEditingDraftItem((prev) => {
            if (!prev) return prev;
            const sameItem = String(prev.dictionaryId || '') === String(baseItem.dictionaryId || '')
                && String(prev.unit || '') === String(baseItem.unit || '');
            if (!sameItem) return prev;
            if (!preference) {
                return {
                    ...prev,
                    learnedPreference: null,
                    learnedPreferenceApplied: false
                };
            }
            const shouldAutoApply = !(Number(prev.perUnitCanonicalAmount || 0) > 0);
            return {
                ...prev,
                learnedPreference: preference,
                learnedPreferenceApplied: shouldAutoApply,
                perUnitCanonicalAmount: shouldAutoApply ? String(preference.multiplier || '') : prev.perUnitCanonicalAmount
            };
        });
    };

    useEffect(() => {
        if (isOpen) {
            setDraftItems([]);
            setListName('');
            setIsPasteMode(initialValue?.length > 0);
            setPasteText(initialValue || '');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen, initialValue]);

    const openResolvedDraftEditor = (item) => {
        const nextItem = hydrateDraftEditorItem({
            name: item.name,
            amount: item.amount || 1,
            unit: item.unit || 'adet',
            dictionaryId: item.dictionaryId || item.id || null,
            brand: item.brand || '',
            alternativeProduct: item.alternativeProduct || '',
            price: item.price || 0,
            defaultPrice: item.defaultPrice || 0,
            defaultPriceUnit: item.defaultPriceUnit || '',
            defaultPriceSource: item.defaultPriceSource || 'NONE',
            requiresBrand: Boolean(item.requiresBrand),
            resolveStatus: 'RESOLVED',
            candidates: []
        });
        setEditingDraftItem(nextItem);
        if (MARKET_UNITS.has(String(nextItem.unit || '').trim()) && nextItem.dictionaryId) {
            void applyLearnedPreferenceToEditor(nextItem);
        }
    };

    const handleSuggestionPick = (suggestion, context = {}) => {
        const parsed = context?.parsed || {};
        openResolvedDraftEditor({
            id: suggestion.id,
            dictionaryId: suggestion.id,
            name: suggestion.name,
            amount: parsed.amount || 1,
            unit: parsed.unit || suggestion.unit || 'adet',
            brand: '',
            alternativeProduct: '',
            price: suggestion.defaultPrice || 0,
            defaultPrice: suggestion.defaultPrice || 0,
            defaultPriceUnit: suggestion.defaultPriceUnit || '',
            defaultPriceSource: suggestion.defaultPriceSource || 'NONE',
            requiresBrand: Boolean(suggestion.requiresBrand),
            resolveStatus: 'RESOLVED',
            type: suggestion.type || 'PRODUCT'
        });
    };

    const saveDraftEditor = () => {
        if (!editingDraftItem) return;
        const normalization = resolveDraftNormalization(editingDraftItem);
        if (normalization.needsClarification && !(Number(editingDraftItem.perUnitCanonicalAmount || 0) > 0)) {
            toast.error(normalization.prompt || 'Lütfen paket karşılığını girin.');
            return;
        }

        setDraftItems((prev) => [...prev, hydrateDraftEditorItem(editingDraftItem)]);
        setEditingDraftItem(null);
    };

    const handleAddItem = async (item) => {
        if (item.resolveStatus === 'RESOLVED') {
            openResolvedDraftEditor(item);
            return;
        }

        if (item.resolveStatus === 'UNKNOWN') {
            try {
                const token = localStorage.getItem('authToken');
                const res = await fetch(`${API_BASE_URL}/api/shopping/unification/resolve-batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ names: [item.name] })
                });
                const results = await res.json();
                const result = results?.[0];

                if (result) {
                    if (result.status === 'RESOLVED') {
                        openResolvedDraftEditor({
                            ...item,
                            name: result.canonicalName || item.name,
                            unit: item.unit || result.canonicalUnit || 'adet',
                            dictionaryId: result.dictionaryId || null,
                            resolveStatus: 'RESOLVED',
                            candidates: []
                        });
                    } else {
                        setDraftItems((prev) => [
                            ...prev,
                            {
                                ...item,
                                dictionaryId: result.status === 'RESOLVED' ? (result.dictionaryId || null) : null,
                                resolveStatus: result.status || 'UNKNOWN',
                                candidates: result.candidates || []
                            }
                        ]);
                    }
                    return;
                }
            } catch (err) {
                console.error('Auto-resolve error:', err);
            }
        }

        setDraftItems((prev) => [...prev, item]);
    };

    const handleResolve = (index, candidate) => {
        const sourceItem = draftItems[index];
        setDraftItems((prev) => prev.filter((_, i) => i !== index));
        openResolvedDraftEditor({
            ...sourceItem,
            dictionaryId: candidate.id,
            resolveStatus: 'RESOLVED',
            name: candidate.canonicalName,
            unit: sourceItem?.unit || candidate.canonicalUnit || 'adet'
        });
    };

    const handleRemove = (index) => {
        setDraftItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpdateItem = (index, updates) => {
        setDraftItems((prev) => {
            const next = [...prev];
            next[index] = applyShoppingSeedUpdate(next[index], updates);
            return next;
        });
    };

    const handlePasteProcess = async () => {
        const lines = pasteText
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length >= 2);

        if (lines.length === 0) {
            setIsPasteMode(false);
            return;
        }

        setIsResolvingBulk(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/shopping/unification/resolve-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ names: lines })
            });
            const results = await res.json();

            const newItems = results.map((result, idx) => {
                const parsed = parseSmartInput(lines[idx]);
                return {
                    name: result.status === 'RESOLVED'
                        ? (result.canonicalName || result.rawName)
                        : (parsed.name || result.rawName),
                    amount: parsed.amount,
                    unit: parsed.unit || result.canonicalUnit,
                    dictionaryId: result.status === 'RESOLVED' ? (result.dictionaryId || null) : null,
                    resolveStatus: result.status || 'UNKNOWN',
                    candidates: result.candidates || []
                };
            });

            setDraftItems((prev) => [...prev, ...newItems]);
            setIsPasteMode(false);
            setPasteText('');
        } catch (err) {
            toast.error('Liste işlenirken bir hata oluştu.');
        } finally {
            setIsResolvingBulk(false);
        }
    };

    const handleSave = async () => {
        if (draftItems.length === 0) {
            toast.error('Lütfen en az bir ürün ekleyin.');
            return;
        }

        const unresolvedKitchenItems = draftItems.filter((item) => {
            const itemGroup = String(item.itemGroup || 'KITCHEN').toUpperCase();
            const resolveStatus = String(item.resolveStatus || '').toUpperCase();
            return itemGroup === 'KITCHEN'
                && (!String(item.dictionaryId || '').trim() || ['UNKNOWN', 'AMBIGUOUS', 'CANDIDATE'].includes(resolveStatus));
        });
        if (unresolvedKitchenItems.length > 0) {
            toast.error('Mutfak ürünleri için önce listeden doğru ürünü seçmelisin.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                name: listName || `Hızlı Liste (${new Date().toLocaleDateString('tr-TR')})`,
                items: draftItems.map((item) => ({
                    dictionaryId: item.dictionaryId,
                    name: item.name,
                    amount: item.amount || 1,
                    unit: item.unit || 'adet',
                    brand: item.brand || '',
                    alternativeProduct: item.alternativeProduct || '',
                    price: item.price || 0,
                    resolveStatus: item.resolveStatus,
                    ...buildDraftNormalizationPayload(item)
                })),
                notification: notificationSettings
            };

            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/shopping/lists`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success('Liste başarıyla oluşturuldu!');
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
                if (onFinish) onFinish();
                onClose();
            } else {
                const err = await res.json();
                toast.error(err.error || 'Liste kaydedilemedi.');
            }
        } catch (error) {
            toast.error('Bağlantı hatası.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const editingDraftItemModal = editingDraftItem && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/40 backdrop-blur-md p-0 sm:items-center sm:p-6">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    className="relative z-[1001] w-full max-w-none rounded-t-[2rem] bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl dark:bg-gray-800 sm:max-w-sm sm:rounded-[2rem] sm:p-6"
                >
                    <div className="text-center">
                        <h3 className="text-xl font-black text-gray-900 dark:text-white">{editingDraftItem.name}</h3>
                        <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">Miktarı ve birimi netleştir</p>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Miktar</label>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={editingDraftItem.amount}
                                onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, amount: e.target.value }))}
                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-lg font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Birim</label>
                            <select
                                value={editingDraftItem.unit}
                                onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, unit: e.target.value }))}
                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-sm font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                            >
                                <option value="adet">adet</option>
                                <option value="kg">kg</option>
                                <option value="gr">gr</option>
                                <option value="lt">lt</option>
                                <option value="ml">ml</option>
                                <option value="paket">paket</option>
                                <option value="demet">demet</option>
                                <option value="kutu">kutu</option>
                                <option value="koli">koli</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button
                            onClick={() => setEditingDraftItem(null)}
                            className="h-12 flex-1 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            İptal
                        </button>
                        <button
                            onClick={saveDraftEditor}
                            className="h-12 flex-[1.4] rounded-2xl bg-primary text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-primary/20"
                        >
                            Ekle
                        </button>
                    </div>
                </motion.div>
            </div>,
            document.body
        )
        : null;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative flex h-[100dvh] w-full max-w-none flex-col overflow-hidden bg-white shadow-2xl dark:bg-gray-900 sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-[2.5rem]"
                >
                    <div className="flex items-center justify-between border-b border-gray-100 p-5 pb-4 dark:border-gray-800 sm:p-8 sm:pb-4">
                        <div>
                            <h2 className="text-2xl font-black uppercase tracking-tighter dark:text-white">Hızlı Liste</h2>
                            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">Omnibar v1.1</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                            <span className="material-symbols-outlined text-gray-400">close</span>
                        </button>
                    </div>

                    <div className="custom-scrollbar flex-1 overflow-y-auto p-5 pt-4 sm:p-8 sm:pt-4">
                        {isPasteMode ? (
                            <div className="space-y-4">
                                <p className="px-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Toplu Liste Yapıştır</p>
                                <textarea
                                    value={pasteText}
                                    onChange={(e) => setPasteText(e.target.value)}
                                    placeholder={'Her satıra bir ürün gelecek şekilde yapıştırın...\n2 kg domates\nYumurta...'}
                                    className="h-64 w-full resize-none rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 p-5 text-sm font-bold transition-all outline-none placeholder:text-gray-300 focus:border-primary dark:border-gray-800 dark:bg-gray-900/50 dark:placeholder:text-gray-700"
                                />
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setIsPasteMode(false)}
                                        className="h-12 flex-1 rounded-2xl border-2 border-gray-100 text-xs font-black uppercase text-gray-400 dark:border-gray-800"
                                    >
                                        Vazgeç
                                    </button>
                                    <button
                                        onClick={handlePasteProcess}
                                        disabled={isResolvingBulk}
                                        className="flex h-12 flex-[2] items-center justify-center gap-2 rounded-2xl bg-primary text-xs font-black uppercase text-white shadow-lg shadow-primary/20"
                                    >
                                        {isResolvingBulk ? 'İşleniyor...' : 'Listeyi Çözümle'}
                                        <span className="material-symbols-outlined text-lg">auto_awesome</span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <SmartInput
                                    onAdd={handleAddItem}
                                    onSelectSuggestion={handleSuggestionPick}
                                    mode="wizard"
                                />

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between px-1">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Eklenen Ürünler ({draftItems.length})</p>
                                        {draftItems.length === 0 && (
                                            <button
                                                onClick={() => setIsPasteMode(true)}
                                                className="text-[10px] font-black uppercase text-primary hover:underline"
                                            >
                                                Toplu Yapıştır
                                            </button>
                                        )}
                                    </div>

                                    <div className="min-h-[60px]">
                                        {draftItems.length === 0 ? (
                                            <div className="py-12 text-center opacity-40">
                                                <span className="material-symbols-outlined mb-2 text-4xl">add_shopping_cart</span>
                                                <p className="text-[10px] font-bold uppercase">Henüz ürün eklenmedi</p>
                                            </div>
                                        ) : (
                                            draftItems.map((item, idx) => (
                                                <DraftItemRow
                                                    key={idx}
                                                    item={item}
                                                    index={idx}
                                                    mode={item.resolveStatus === 'RESOLVED' ? 'summary' : 'edit'}
                                                    onResolve={(candidate) => handleResolve(idx, candidate)}
                                                    onRemove={() => handleRemove(idx)}
                                                    onUpdate={(updates) => handleUpdateItem(idx, updates)}
                                                    onEdit={() => openResolvedDraftEditor(item)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>

                                {draftItems.length > 0 && (
                                    <div className="space-y-4 border-t border-gray-100 pt-4 dark:border-gray-800">
                                        <input
                                            value={listName}
                                            onChange={(e) => setListName(e.target.value)}
                                            placeholder="Liste Adı (Opsiyonel)"
                                            className="h-12 w-full rounded-2xl border-2 border-gray-100 bg-gray-50 px-5 text-[10px] font-black uppercase transition-all outline-none focus:border-primary dark:border-gray-800 dark:bg-gray-900/50"
                                        />
                                        <NotificationSelector onChange={setNotificationSettings} initialData={notificationSettings} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-gray-100 bg-white p-5 pt-4 dark:border-gray-800 dark:bg-gray-900 sm:border-t-0 sm:bg-transparent sm:p-8 sm:pt-4">
                        <button
                            onClick={handleSave}
                            disabled={isSubmitting || draftItems.length === 0 || isPasteMode}
                            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-xs font-black uppercase tracking-widest text-white shadow-xl shadow-primary/30 transition-all active:scale-[0.98] disabled:grayscale disabled:opacity-50 hover:bg-black"
                        >
                            {isSubmitting ? 'Kaydediliyor...' : 'Listeyi Tamamla'}
                            <span className="material-symbols-outlined text-lg">check_circle</span>
                        </button>
                    </div>

                    <AnimatePresence>
                        {editingDraftItem && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 z-[240] flex items-end justify-center bg-black/45 backdrop-blur-md p-0 sm:items-center sm:p-6"
                            >
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.97, y: 10 }}
                                    className="relative w-full max-w-none rounded-t-[2rem] bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl dark:bg-gray-800 sm:max-w-sm sm:rounded-[2rem] sm:p-6"
                                >
                                    <div className="text-center">
                                        <h3 className="text-xl font-black text-gray-900 dark:text-white">{editingDraftItem.name}</h3>
                                        <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-gray-400">Miktarı ve birimi netleştir</p>
                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-3">
                                        <div className="space-y-2">
                                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Miktar</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                value={editingDraftItem.amount}
                                                onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, amount: e.target.value }))}
                                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-lg font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Birim</label>
                                            <select
                                                value={editingDraftItem.unit}
                                                onChange={(e) => setEditingDraftItem((prev) => {
                                                    const next = { ...prev, unit: e.target.value };
                                                    const normalization = resolveDraftNormalization(next);
                                                    const hydratedNext = {
                                                        ...next,
                                                        measurementType: normalization.measurementType,
                                                        expectedCanonicalUnit: normalization.expectedCanonicalUnit,
                                                        perUnitCanonicalAmount: normalization.needsClarification &&
                                                            normalization.expectedCanonicalUnit === prev.expectedCanonicalUnit
                                                            ? prev.perUnitCanonicalAmount
                                                            : '',
                                                        learnedPreference: null,
                                                        learnedPreferenceApplied: false
                                                    };
                                                    if (MARKET_UNITS.has(String(hydratedNext.unit || '').trim()) && hydratedNext.dictionaryId) {
                                                        void applyLearnedPreferenceToEditor(hydratedNext);
                                                    }
                                                    return {
                                                        ...hydratedNext
                                                    };
                                                })}
                                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-sm font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                                            >
                                                <option value="adet">adet</option>
                                                <option value="kg">kg</option>
                                                <option value="gr">gr</option>
                                                <option value="lt">lt</option>
                                                <option value="ml">ml</option>
                                                <option value="paket">paket</option>
                                                <option value="demet">demet</option>
                                                <option value="kutu">kutu</option>
                                                <option value="koli">koli</option>
                                            </select>
                                        </div>
                                    </div>

                                    {resolveDraftNormalization(editingDraftItem).needsClarification && (
                                        <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300">
                                                {resolveDraftNormalization(editingDraftItem).prompt}
                                            </p>
                                            {editingDraftItem.learnedPreference && (
                                                <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900/30 dark:bg-emerald-950/20">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                                                                Geçen sefer
                                                            </p>
                                                            <p className="mt-1 text-[11px] font-bold text-emerald-900 dark:text-emerald-100">
                                                                1 {editingDraftItem.unit} = {editingDraftItem.learnedPreference.multiplier} {editingDraftItem.learnedPreference.canonicalUnit}
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingDraftItem((prev) => ({
                                                                ...prev,
                                                                perUnitCanonicalAmount: String(prev.learnedPreference?.multiplier || ''),
                                                                learnedPreferenceApplied: true
                                                            }))}
                                                            className="rounded-2xl bg-emerald-600 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-emerald-600/20"
                                                        >
                                                            Uygula
                                                        </button>
                                                    </div>
                                                    <p className="mt-2 text-[10px] font-medium text-emerald-700/80 dark:text-emerald-200/80">
                                                        {editingDraftItem.learnedPreferenceApplied
                                                            ? 'Bu öneri otomatik dolduruldu, istersen değiştirebilirsin.'
                                                            : 'Dilersen aynı karşılığı tek tıkla yeniden kullanabilirsin.'}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="mt-3 flex items-center gap-3">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={editingDraftItem.perUnitCanonicalAmount || ''}
                                                    onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, perUnitCanonicalAmount: e.target.value }))}
                                                    placeholder="Örn: 500"
                                                    className="h-12 flex-1 rounded-2xl bg-white px-4 text-lg font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                                                />
                                                <div className="flex h-12 min-w-[72px] items-center justify-center rounded-2xl bg-white px-4 text-xs font-black uppercase text-amber-700 dark:bg-gray-900 dark:text-amber-300">
                                                    {resolveDraftNormalization(editingDraftItem).expectedCanonicalUnit}
                                                </div>
                                            </div>
                                            <p className="mt-3 text-[11px] font-bold text-amber-800/80 dark:text-amber-200/80">
                                                {resolveDraftNormalizationPreview(editingDraftItem)?.text || resolveDraftNormalization(editingDraftItem).helperText}
                                            </p>
                                        </div>
                                    )}

                                    <div className="mt-4 space-y-3">
                                        <div className="space-y-2">
                                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Marka</label>
                                            <input
                                                type="text"
                                                value={editingDraftItem.brand || ''}
                                                onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, brand: e.target.value }))}
                                                placeholder="Örn: Sütaş"
                                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-sm font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-gray-400">Alternatif Marka</label>
                                            <input
                                                type="text"
                                                value={editingDraftItem.alternativeProduct || ''}
                                                onChange={(e) => setEditingDraftItem((prev) => ({ ...prev, alternativeProduct: e.target.value }))}
                                                placeholder="Örn: İçim"
                                                className="h-12 w-full rounded-2xl bg-gray-50 px-4 text-sm font-black text-gray-900 outline-none dark:bg-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 flex gap-3">
                                        <button
                                            onClick={() => setEditingDraftItem(null)}
                                            className="h-12 flex-1 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            İptal
                                        </button>
                                        <button
                                            onClick={saveDraftEditor}
                                            className="h-12 flex-[1.4] rounded-2xl bg-primary text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-primary/20"
                                        >
                                            Ekle
                                        </button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default QuickListEntryModal;
